import type { Instance } from "tippy.js";

/**
 * tippy.js has no `disabled` prop (only the imperative `.disable()`/`.enable()`
 * methods), so a `disabled` option passed to the directive/component is silently
 * dropped — `evaluateProps` keeps the key on `instance.props`, but nothing acts on
 * it. Map it onto the real methods so `v-tippy="{ content, disabled }"` works as
 * expected everywhere.
 *
 * Wired into `defaultProps.onCreate` + `onAfterUpdate`: `instance.props` is already
 * updated when both hooks run, and the directive re-applies `setProps` on every
 * re-render, so it stays reactive to the bound `disabled` value. `disable()` also
 * hides an already-open tooltip when the flag flips to `true`.
 */
export const syncTippyDisabled = (instance: Instance): void => {
  if ((instance.props as { disabled?: boolean }).disabled) {
    instance.disable();
  } else {
    instance.enable();
  }
};

export const tippyOptions = {
  directive: "tippy",
  component: "tippy",
  defaultProps: {
    placement: "top",
    animation: "shift-away",
    arrow: true,
    delay: [150, 80],
    duration: [160, 100],
    theme: "orbis",
    interactive: false,
    hideOnClick: false,
    touch: "hold" as const,
    onCreate: syncTippyDisabled,
    onAfterUpdate: syncTippyDisabled,
    popperOptions: {
      strategy: "fixed" as const,
      modifiers: [
        { name: "preventOverflow", options: { boundary: "viewport", padding: 8 } },
        { name: "flip", options: { boundary: "viewport", padding: 8 } },
      ],
    },
  },
};
